<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  campaignData: {
    type: Object,
    required: true,
  },
});

const repeatTypeLabel = computed(() => {
  const key = (props.campaignData.repeatType || 'once').toUpperCase();
  return t(`CAMPAIGN.V2.REPEAT_TYPES.${key}`);
});

const templateName = computed(() => {
  if (props.campaignData.inboxType === 'whatsapp') {
    return props.campaignData.template?.name || '-';
  }
  const msg = props.campaignData.message || '';
  return msg.length > 80 ? `${msg.substring(0, 80)}...` : msg || '-';
});

const limitsText = computed(() => {
  const parts = [];
  if (props.campaignData.sendLimitTotal) {
    parts.push(
      t('CAMPAIGN.V2.WIZARD.REVIEW.TOTAL_LIMIT', {
        limit: props.campaignData.sendLimitTotal,
      })
    );
  }
  if (props.campaignData.sendLimitPerRun) {
    parts.push(
      t('CAMPAIGN.V2.WIZARD.REVIEW.PER_RUN_LIMIT', {
        limit: props.campaignData.sendLimitPerRun,
      })
    );
  }
  return parts.length ? parts.join(' | ') : t('CAMPAIGN.V2.WIZARD.REVIEW.UNLIMITED');
});
</script>

<template>
  <div class="max-w-2xl">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.V2.WIZARD.REVIEW.TITLE') }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('CAMPAIGN.V2.WIZARD.REVIEW.DESCRIPTION') }}
    </p>

    <div class="mt-6 space-y-4">
      <div class="p-4 rounded-lg bg-n-alpha-1 border border-n-weak space-y-3">
        <!-- Title -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ campaignData.title || '-' }}
          </span>
        </div>

        <!-- Inbox -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.INBOX_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ campaignData.inbox?.name || '-' }}
          </span>
        </div>

        <!-- Audience -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.AUDIENCE_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ campaignData.audience?.name || '-' }}
          </span>
        </div>

        <!-- Template -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.TEMPLATE_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ templateName }}
          </span>
        </div>

        <!-- Repeat -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.REPEAT_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ repeatTypeLabel }}
          </span>
        </div>

        <!-- Scheduled At -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.SCHEDULE_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ campaignData.scheduledAt || '-' }}
          </span>
        </div>

        <!-- Limits -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.LIMITS_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ limitsText }}
          </span>
        </div>

        <!-- Allow Resend -->
        <div class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.REVIEW.ALLOW_RESEND_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{
              campaignData.allowResend
                ? t('CAMPAIGN.V2.WIZARD.REVIEW.YES')
                : t('CAMPAIGN.V2.WIZARD.REVIEW.NO')
            }}
          </span>
        </div>

        <!-- Dispatch Time -->
        <div v-if="campaignData.dispatchTime" class="flex justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.DISPATCH_TIME_LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ campaignData.dispatchTime }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
